/* eslint-disable react-hooks/exhaustive-deps */
'use client'
import { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react'
import useCarousel from 'hooks/useCarousel'
import useInactivityStatus from 'hooks/useInactivityStatus'
import classNames from 'classnames'
import { Typography, MediaImageStandard, LinkButton } from 'components/Contentful'
import Ok from 'components/Svgs/Ok'
import { BLOCKS } from '@contentful/rich-text-types'
import ThumbUp from 'components/Svgs/ThumbUp'
import { AchieveLink } from 'components/AchieveLink'

const AUTO_PLAY_DELAY = 6000 // 6 seconds
const DEFAULT_DATA_TEST_ID = 'hero-carousel-timer'

// Default SVG paths - can be overridden via props
const DEFAULT_SVG_PATHS = {
  chevronLeft: 'M 7.43749 1.06421 L 1.06421 7.43749 L 7.43749 13.8108',
  pauseLeft: 'M 18 2 L 18 13',
  pauseRight: 'M 23 2 L 23 13',
  play: 'M 10 2 L 10 13 L 18 7.5 Z',
}

/**
 * CarouselControls component - provides navigation and playback controls
 * Includes previous/next buttons, play/pause toggle, and pagination dots with progress indicators
 */
function CarouselControls({
  onPrevious,
  onNext,
  onPlayPause,
  onDotClick,
  currentIndex,
  isPlaying,
  totalSlides,
  prefersReducedMotion,
  progressKey,
  autoPlayDelay = AUTO_PLAY_DELAY,
  svgPaths = DEFAULT_SVG_PATHS,
  dataTestId = DEFAULT_DATA_TEST_ID,
}) {
  return (
    <div
      className="flex gap-[12px] items-center relative"
      data-name="player"
      data-testid={`${dataTestId}-controls`}
    >
      {/* Previous Button */}
      <button
        onClick={onPrevious}
        aria-label="Previous slide"
        data-testid={`${dataTestId}-arrow-left`}
        className="bg-white size-[40px] border-solid relative rounded-full flex items-center justify-center border border-[#c0cbd8]"
      >
        <svg className="w-[10px] h-[16px]" fill="none" viewBox="0 0 8.50156 14.8745">
          <path
            d={svgPaths.chevronLeft}
            stroke="#154199"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2.12858"
          />
        </svg>
      </button>

      {/* Pause/Play Button */}
      {!prefersReducedMotion && (
        <button
          onClick={onPlayPause}
          aria-label={isPlaying ? 'Stop automatic slide show' : 'Start automatic slide show'}
          data-testid={`${dataTestId}-play-pause`}
          className="bg-white size-[40px] border-solid relative rounded-full flex items-center justify-center border border-[#c0cbd8]"
        >
          {isPlaying ? (
            <svg className="w-[13] h-[17px]" fill="none" viewBox="0 0 15 15">
              <path
                d="M12 0.833374H10.3333C9.8731 0.833374 9.5 1.20647 9.5 1.66671V13.3334C9.5 13.7936 9.8731 14.1667 10.3333 14.1667H12C12.4602 14.1667 12.8333 13.7936 12.8333 13.3334V1.66671C12.8333 1.20647 12.4602 0.833374 12 0.833374Z"
                stroke="#154199"
                strokeWidth="1.66667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4.66797 0.833374H3.0013C2.54107 0.833374 2.16797 1.20647 2.16797 1.66671V13.3334C2.16797 13.7936 2.54107 14.1667 3.0013 14.1667H4.66797C5.12821 14.1667 5.5013 13.7936 5.5013 13.3334V1.66671C5.5013 1.20647 5.12821 0.833374 4.66797 0.833374Z"
                stroke="#154199"
                strokeWidth="1.66667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg className="w-[13px] h-[17px] ml-[2px]" fill="none" viewBox="0 0 15 15">
              <path
                d="M11.7119 9.82685L3.15291 15.865C1.82803 16.7996 0 15.8521 0 14.2307V8.08923V2.00354C0 0.371472 1.84939 -0.573464 3.17192 0.382864L11.7309 6.57192C12.843 7.37606 12.8333 9.03575 11.7119 9.82685Z"
                fill="#154199"
              />
            </svg>
          )}
        </button>
      )}

      {/* Pagination Dots */}
      <div
        className="flex gap-[6px] items-center ml-2"
        role="tablist"
        aria-label="Slide selection"
        data-testid={`${dataTestId}-dots`}
      >
        {Array.from({ length: totalSlides }).map((_, index) => (
          <button
            key={index}
            onClick={() => onDotClick(index)}
            aria-label={`Go to slide ${index + 1}`}
            aria-selected={currentIndex === index}
            role="tab"
            data-testid={`${dataTestId}-dot-${index}`}
            className={classNames(
              'rounded-full shrink-0 transition-all focus:outline-hidden  border-transparent ',
              currentIndex === index
                ? 'bg-[#d1d5dc] flex flex-col h-[6px] items-start overflow-hidden relative w-[48px] p-0 border-0!'
                : 'bg-[#c0cbd8] size-[6px] py-px px-[2px]'
            )}
            style={{
              '--progress-duration': `${autoPlayDelay}ms`,
              ...(currentIndex === index && { border: 0 }),
            }}
          >
            {currentIndex === index && isPlaying && !prefersReducedMotion && (
              <div
                key={progressKey}
                className="bg-[#3300ff] h-full carousel-progress-fill"
                style={{ border: 0 }}
                data-name="Container"
              />
            )}
          </button>
        ))}
      </div>

      {/* Next Button */}
      <button
        onClick={onNext}
        aria-label="Next slide"
        data-testid={`${dataTestId}-arrow-right`}
        className="bg-white size-[40px] border-solid relative rounded-full flex items-center justify-center border border-[#c0cbd8]"
      >
        <svg className="w-[10px] h-[16px] rotate-180" fill="none" viewBox="0 0 8.50156 14.8745">
          <path
            d={svgPaths.chevronLeft}
            stroke="#154199"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2.12858"
          />
        </svg>
      </button>
    </div>
  )
}

/**
 * Slide component - renders individual carousel slide
 */
function Slide({
  slide,
  isActive,
  renderSlide,
  preload = false,
  dataTestId = DEFAULT_DATA_TEST_ID,
}) {
  // If custom render function provided, use it
  if (typeof renderSlide === 'function') {
    return renderSlide(slide, isActive)
  }

  // Default slide rendering
  return (
    <div
      className="relative w-full h-full px-1 sm:px-[30px] lg:px-0"
      data-testid={`${dataTestId}-slide-content`}
    >
      <div className="flex flex-col lg:flex-row justify-between p-0 lg:p-0 lg:items-stretch pt-[23px] lg:pt-0 items-center lg:h-[655px]">
        <div className="max-w-full xl:max-w-[490px]">
          <div className="lg:w-[490px] lg:py-16">
            <Typography
              content={slide.title}
              responsive={true}
              className={`transition-all duration-100 transition-discrete ease-in-out text-left text-[#1D252F] font-bold text-[32px] leading-[1.3] min-[360px]:text-[40px] min-[440px]:text-[52px] min-[440px]:leading-[1.4] lg:text-[56px] lg:leading-[1.05] lg:tracking-[-0.02em]`}
            />
            {slide?.factsList?.length > 0 && (
              <div className="flex items-center justify-start py-7 sm:py-7">
                <ul className="m-0 p-0 transition-all duration-100 transition-discrete ease-in-out">
                  {slide.factsList.map((icon, pos) => {
                    return (
                      <li
                        key={pos}
                        className="flex flex-row flex-nowrap text-left mb-2 md:mb-[18px] justify-start transition-all duration-100 transition-discrete ease-in-out items-center"
                      >
                        <Ok className="h-[25px] w-[25px] text-[#1D252F]" />
                        <Typography
                          responsive={true}
                          variant="bodyLg"
                          mobileVariant="bodySm"
                          className="pl-2"
                          content={icon}
                        />
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}
            {slide?.ctaURL && (
              <LinkButton
                track={{
                  ...{
                    list_name: 'Tab CTA click',
                    click_id: slide?.ctaText,
                    click_text: slide?.ctaText,
                    click_url: slide?.ctaURL,
                    track_event: 'internal_campaign_click',
                    event_action: 'button_click',
                    event_type: 'button_click',
                  },
                  ...slide?.ctaTrack,
                }}
                data-testid="slider-cta-button"
                typographicOptions={{
                  variantOverride: { [BLOCKS.PARAGRAPH]: 'bodyLg' },
                  fontWeight: 'bold',
                }}
                className="transition-all duration-100 transition-discrete ease-in-out"
                content={{
                  linkHref: slide?.ctaURL,
                  linkText: slide?.ctaText,
                }}
                fullWidth={false}
              />
            )}

            {slide?.extraText && (
              <li
                className={`${
                  slide?.disclaimerText ? 'pt-4' : 'pt-7'
                } flex flex-row text-left mb-2 justify-start transition-all duration-100 transition-discrete ease-in-out`}
              >
                <ThumbUp className="h-[25px] w-[25px] shrink-0" />
                <Typography
                  className="text-[12px] text-[#5C708A] min-[375px]:whitespace-nowrap"
                  content={slide?.extraText}
                />
              </li>
            )}

            {slide?.disclaimerText && (
              <div className="flex items-baseline gap-1 flex-wrap">
                <Typography
                  component="span"
                  responsive={true}
                  className={
                    slide?.disclaimerTextClassName ||
                    'font-ultramarine text-[16px] font-normal leading-[140%] text-left text-[#1D252F]'
                  }
                  content={slide?.disclaimerText}
                />
                {slide?.disclaimerLinkText && slide?.disclaimerLinkHref && (
                  <AchieveLink
                    href={slide?.disclaimerLinkHref}
                    track={{
                      list_name: 'Home Hero Direct Mailer Click',
                      click_id: slide?.disclaimerLinkText,
                      click_text: slide?.disclaimerLinkText,
                      click_url: slide?.disclaimerLinkHref,
                    }}
                  >
                    <Typography
                      component="span"
                      responsive={true}
                      className={
                        slide?.disclaimerLinkTextClassName ||
                        'font-ultramarine text-[16px] font-normal leading-[140%] text-left text-[#3300FF] underline'
                      }
                      content={slide?.disclaimerLinkText}
                    />
                  </AchieveLink>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="overflow-hidden rounded-md relative hidden lg:flex items-end lg:pt-8 lg:pb-0 lg:w-auto lg:flex-1 lg:h-full">
          <MediaImageStandard
            content={{ url: slide?.image, fileName: slide?.alt }}
            className="w-full rounded-md lg:max-w-none lg:w-full lg:h-full object-contain"
            height={441}
            width={339}
            alt={slide?.alt || 'Carousel image'}
            fetchPriority="high"
            preload={preload}
          />
        </div>
      </div>
    </div>
  )
}

/**
 * HeroCarouselTimer - Infinite scrolling carousel with timer controls
 *
 * Features:
 * - Infinite scroll effect using cloned slides
 * - Auto-play with configurable delay
 * - Stops permanently on user interaction
 * - Respects prefers-reduced-motion
 * - Keyboard navigation (arrow keys)
 * - Controls positioned absolutely to avoid layout shift
 * - Mobile: Image hidden, controls at bottom
 * - Desktop: Image fills 660px height, controls overlaid at bottom-left
 *
 * @param {Array} slides - Array of slide objects with { id, title, image, alt, factsList, ctaURL, ctaText, extraText }
 * @param {Object} svgPaths - Custom SVG paths for controls (chevronLeft, pauseLeft, pauseRight, play)
 * @param {number} autoPlayDelay - Delay in milliseconds between slides (default 6000)
 * @param {Function} renderSlide - Custom render function for slides (slide, isActive) => ReactNode
 * @param {string} classNameContainer - Custom className for carousel container
 * @param {string} classNameTrack - Custom className for carousel track
 * @param {string} classNameSlide - Custom className for individual slides
 */
export function HeroCarouselTimer({
  slides = [],
  svgPaths = DEFAULT_SVG_PATHS,
  autoPlayDelay = AUTO_PLAY_DELAY,
  renderSlide,
  classNameContainer,
  classNameTrack,
  classNameSlide,
  dataTestId = DEFAULT_DATA_TEST_ID,
}) {
  const [isPlaying, setIsPlaying] = useState(true)
  const [hasUserInteracted, setHasUserInteracted] = useState(false)
  const isUserActive = useInactivityStatus()
  const progressKeyRef = useRef(0)
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const autoPlayTimerRef = useRef(null)
  // False on server and first client render so SSR HTML starts at slide 1 (no clone-last).
  // useLayoutEffect flips it to true before the browser paints, inserting clone-last and
  // adjusting scroll position — both in the same synchronous commit cycle.
  const [isMounted, setIsMounted] = useState(false)

  // Check for prefers-reduced-motion
  const prefersReducedMotion =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

  // Full extended slides array with clones: [clone-last, ...slides, clone-first]
  const extendedSlides = [
    { ...slides[slides.length - 1], id: `clone-last-${slides.length - 1}` },
    ...slides,
    { ...slides[0], id: 'clone-first-0' },
  ]

  // SSR / first-render: no clone-last so slide 1 is at scroll position 0 (good for LCP).
  // After mount: switch to extendedSlides (clone-last prepended) for infinite scroll.
  const slidesToRender = isMounted
    ? extendedSlides
    : [...slides, { ...slides[0], id: 'clone-first-0' }]

  // Use the carousel hook for scroll management and intersection observation
  const { containerRef, slideRefs, activeIndex, scrollToSlide, setIsPaused } = useCarousel({
    itemsLength: extendedSlides.length,
    autoPlay: false, // Manual autoplay for better control over infinite scroll transitions
    interval: autoPlayDelay,
    loop: false, // Disabled - we implement infinite scroll with cloned slides
    threshold: 0.5,
  })

  // Phase 1: switch to extendedSlides before paint (triggers synchronous re-render)
  useLayoutEffect(() => {
    setIsMounted(true)
  }, [])

  // Phase 2: after extendedSlides DOM is committed, scroll to slide 1 (index 1)
  // Both phases run in the same pre-paint commit cycle — no visible flash.
  useLayoutEffect(() => {
    if (!isMounted) return
    if (containerRef.current && slideRefs.current[1]) {
      const container = containerRef.current
      const slide = slideRefs.current[1]
      container.style.scrollBehavior = 'auto'
      container.scrollLeft = slide.offsetLeft - container.offsetLeft
      container.style.scrollBehavior = 'smooth'
    }
  }, [isMounted])

  // Update progress key when slide changes
  useEffect(() => {
    progressKeyRef.current += 1
  }, [currentSlideIndex])

  // Handle infinite loop - detect clone slides and instantly jump to real ones
  // This creates the seamless infinite scroll effect by:
  // 1. Detecting when user scrolls to a cloned slide
  // 2. Waiting for scroll to settle (150ms debounce)
  // 3. Instantly jumping to the corresponding real slide without animation
  // 4. Re-enabling smooth scroll for next interaction
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    let isScrolling = false
    let scrollEndTimer = null

    const handleScroll = () => {
      if (isScrolling) return

      if (scrollEndTimer) clearTimeout(scrollEndTimer)

      scrollEndTimer = setTimeout(() => {
        const containerWidth = container.offsetWidth
        const scrollLeft = container.scrollLeft
        const slideWidth = slideRefs.current[1]?.offsetWidth || containerWidth
        const currentSlideIndex = Math.round(scrollLeft / slideWidth)

        // At clone of first slide (end of array) - jump to real first slide
        if (currentSlideIndex === extendedSlides.length - 1 && slideRefs.current[1]) {
          isScrolling = true
          const slide = slideRefs.current[1]
          container.style.scrollBehavior = 'auto'
          container.scrollLeft = slide.offsetLeft
          setTimeout(() => {
            container.style.scrollBehavior = 'smooth'
            isScrolling = false
          }, 50)
        }
        // At clone of last slide (start of array) - jump to real last slide
        else if (currentSlideIndex === 0 && slideRefs.current[slides.length]) {
          isScrolling = true
          const slide = slideRefs.current[slides.length]
          container.style.scrollBehavior = 'auto'
          container.scrollLeft = slide.offsetLeft
          setTimeout(() => {
            container.style.scrollBehavior = 'smooth'
            isScrolling = false
          }, 50)
        }
      }, 150)
    }

    container.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      container.removeEventListener('scroll', handleScroll)
      if (scrollEndTimer) clearTimeout(scrollEndTimer)
    }
  }, [extendedSlides.length, slides.length])

  // Update current slide index based on active index
  useEffect(() => {
    // Update current slide index (map extended index to real index)
    if (activeIndex >= 1 && activeIndex <= slides.length) {
      setCurrentSlideIndex(activeIndex - 1)
    } else if (activeIndex === 0) {
      setCurrentSlideIndex(slides.length - 1)
    } else if (activeIndex === extendedSlides.length - 1) {
      setCurrentSlideIndex(0)
    }
  }, [activeIndex, extendedSlides.length, slides.length])

  // Custom scroll next/prev
  const scrollToNext = useCallback(() => {
    const nextIndex = activeIndex + 1
    if (nextIndex < extendedSlides.length) {
      scrollToSlide(nextIndex)
    }
  }, [activeIndex, extendedSlides.length, scrollToSlide])

  const scrollToPrev = useCallback(() => {
    const prevIndex = activeIndex - 1
    if (prevIndex >= 0) {
      scrollToSlide(prevIndex)
    }
  }, [activeIndex, scrollToSlide])

  // Autoplay functionality
  useEffect(() => {
    if (!isPlaying || !isUserActive || hasUserInteracted || prefersReducedMotion) {
      if (autoPlayTimerRef.current) {
        clearInterval(autoPlayTimerRef.current)
        autoPlayTimerRef.current = null
      }
      return
    }

    autoPlayTimerRef.current = setInterval(() => {
      scrollToNext()
    }, autoPlayDelay)

    return () => {
      if (autoPlayTimerRef.current) {
        clearInterval(autoPlayTimerRef.current)
        autoPlayTimerRef.current = null
      }
    }
  }, [
    isPlaying,
    isUserActive,
    hasUserInteracted,
    prefersReducedMotion,
    autoPlayDelay,
    scrollToNext,
  ])

  // Handle user interaction to stop autoplay permanently
  const handleUserInteraction = useCallback(() => {
    if (!hasUserInteracted) {
      setHasUserInteracted(true)
      setIsPlaying(false)
      setIsPaused(true)
    }
  }, [hasUserInteracted, setIsPaused])

  // Navigation handlers that stop autoplay on interaction
  const handlePrevious = useCallback(() => {
    handleUserInteraction()
    scrollToPrev()
  }, [handleUserInteraction, scrollToPrev])

  const handleNext = useCallback(() => {
    handleUserInteraction()
    scrollToNext()
  }, [handleUserInteraction, scrollToNext])

  const handleDotClick = useCallback(
    (index) => {
      handleUserInteraction()
      // Add 1 to skip the clone at the beginning of extendedSlides array
      scrollToSlide(index + 1)
    },
    [handleUserInteraction, scrollToSlide]
  )

  // Toggle play/pause - allows user to resume autoplay after manual interaction
  const togglePlayPause = useCallback(() => {
    const newPlayState = !isPlaying
    setIsPlaying(newPlayState)
    setIsPaused(!newPlayState)
    // Allow autoplay to resume when user clicks play
    if (newPlayState) {
      setHasUserInteracted(false)
    }
  }, [isPlaying, setIsPaused])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        handlePrevious()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        handleNext()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handlePrevious, handleNext])

  // Stop autoplay on focus
  const handleFocus = useCallback(() => {
    if (isPlaying) {
      setIsPaused(true)
    }
  }, [isPlaying, setIsPaused])

  const handleBlur = useCallback(() => {
    if (isPlaying && !hasUserInteracted) {
      setIsPaused(false)
    }
  }, [isPlaying, hasUserInteracted, setIsPaused])

  // Render slide content (controls are rendered separately, absolutely positioned)
  const renderSlideContent = useCallback(
    (slide, _index, isActive) => {
      // If slide has a custom component, render it
      if (slide.component) {
        const SlideComponent = slide.component
        return <SlideComponent />
      }

      // Clones are created with spread (new objects) so only the original slides[0]
      // reference matches — correct regardless of id presence or uniqueness.
      return (
        <Slide
          slide={slide}
          isActive={isActive}
          renderSlide={renderSlide}
          fetchPriority="high"
          preload={true}
          loading="eager"
          dataTestId={dataTestId}
        />
      )
    },
    [dataTestId, renderSlide, slides]
  )

  // Check if current slide has disclaimer to adjust controls position
  const currentSlideHasDisclaimer = slides[currentSlideIndex]?.disclaimerText

  return (
    <div
      className={classNames('w-full max-w-[1440px] mx-auto', classNameContainer)}
      role="region"
      aria-roledescription="carousel"
      data-testid={dataTestId}
      onFocus={handleFocus}
      onBlur={handleBlur}
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr)',
        padding: '0 24px',
      }}
    >
      <div className="relative hero-carousel-timer" style={{ gridColumn: '1 / -1' }}>
        <div
          ref={containerRef}
          data-testid={`${dataTestId}-track`}
          className={classNames(
            'w-full flex overflow-x-scroll snap-x snap-mandatory scrollbar-hide scroll-smooth',
            classNameTrack
          )}
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => isPlaying && !hasUserInteracted && setIsPaused(false)}
          onTouchStart={() => setIsPaused(true)}
          onTouchEnd={() => isPlaying && !hasUserInteracted && setIsPaused(false)}
        >
          {slidesToRender.map((slide, index) => {
            // When mounted: [clone-last(0), slide1(1), ..., slideN(N), clone-first(N+1)]
            // When not mounted (SSR): [slide1(0), ..., slideN(N-1), clone-first(N)]
            const realIndex = isMounted
              ? index === 0
                ? slides.length
                : index === extendedSlides.length - 1
                ? 1
                : index
              : index < slides.length
              ? index + 1
              : 1
            return (
              <div
                key={slide.id || index}
                data-index={index}
                data-real-slide-index={realIndex}
                data-testid={`${dataTestId}-slide-${index}`}
                ref={(el) => (slideRefs.current[index] = el)}
                role="group"
                aria-roledescription="slide"
                aria-label={`Slide ${realIndex} of ${slides.length}`}
                className={classNames('snap-center shrink-0 w-full h-full', classNameSlide)}
              >
                {renderSlideContent(slide, index, activeIndex === index)}
              </div>
            )
          })}
        </div>

        {/* Mobile: in flow below the track (sized by the tallest slide), so the controls sit at
            the same spot on every slide, 24px from the content as in Figma. Desktop: overlaid. */}
        <div
          className={`relative my-6 pl-1 sm:pl-[30px] lg:absolute lg:my-0 ${
            currentSlideHasDisclaimer ? 'lg:bottom-14' : 'lg:bottom-16'
          } lg:left-0 lg:w-auto z-10 lg:pl-0`}
          data-testid={`${dataTestId}-controls-container`}
        >
          <CarouselControls
            onPrevious={handlePrevious}
            onNext={handleNext}
            onPlayPause={togglePlayPause}
            onDotClick={handleDotClick}
            currentIndex={currentSlideIndex}
            isPlaying={isPlaying && isUserActive && !hasUserInteracted}
            totalSlides={slides.length}
            prefersReducedMotion={prefersReducedMotion}
            progressKey={progressKeyRef.current}
            autoPlayDelay={autoPlayDelay}
            svgPaths={svgPaths}
            dataTestId={dataTestId}
          />
        </div>
      </div>
    </div>
  )
}

export default HeroCarouselTimer
